import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Button } from 'blaze-ui/Button'
import { Menu } from 'components/Menu/Menu'
import { SelectableItem } from 'components/Menu/MenuItem'
import { Key } from 'react'
import { BrandKitLogo } from 'screens/BrandKit/BrandKitLogo/BrandKitLogo'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import Styles from './BrandKitField.module.scss'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { Brand } from 'blaze-ui/icons'

import { Item, Section } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'
import { useOpenBrandKitCreationModal } from 'hooks/useOpenBrandKitCreationModal'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'

interface Props extends Pick<ButtonProps, 'size'> {
  onChange?: (brandKitId?: number) => void
  isDisabled?: boolean
  value?: number
  allowCreate?: boolean
}

const BrandKitField = ({ size = 'lg', onChange, isDisabled, value, allowCreate = true }: Props) => {
  const { isMobile } = useResponsiveDesign()

  const { data: brandKits, isLoading } = useBrandKits()
  const selectedBrand = brandKits?.find((brandKit) => brandKit.id === value)

  const { openBrandKitCreationModal } = useOpenBrandKitCreationModal({ newStack: true })

  const handleAction = (key: Key) => {
    if (key === 'create') {
      openBrandKitCreationModal()
      return
    }

    const brandKitId = parseInt(key as string)
    onChange?.(value === brandKitId ? undefined : brandKitId)
  }

  if (isLoading) {
    return (
      <Button variant="secondary" size={size} fullWidth={isMobile} isDisabled>
        ...
      </Button>
    )
  }

  if (brandKits?.length === 0) {
    return (
      <Button
        isDisabled={isDisabled}
        variant="secondary"
        size={size}
        fullWidth={isMobile}
        frontIcon={Brand}
        onPress={() => openBrandKitCreationModal()}
      >
        Create New Brand Kit
      </Button>
    )
  }
  if (brandKits?.length === 1 && !allowCreate) {
    const brandKit = brandKits[0]

    return (
      <div className={cx(Styles.brandKitItem, Styles.static)}>
        <BrandKitLogo brandKit={brandKit} size={20} />
        <Text variant="smallList" color="var(--dark-80)" lineClamp={1}>
          {brandKit.name}
        </Text>
      </div>
    )
  }

  return (
    <MenuTrigger popoverClassName={Styles.popover}>
      <Button
        variant="secondary"
        withChevron="down"
        frontIcon={selectedBrand ? () => <BrandKitLogo brandKit={selectedBrand} size={20} /> : undefined}
        size={size}
        fullWidth={isMobile}
        isDisabled={isDisabled}
      >
        <span className={Styles.buttonText}>{selectedBrand?.name || 'Select Brand Kit'}</span>
      </Button>
      <Menu
        onAction={handleAction}
        aria-label="Brand Kit"
        showCheckmark
        selectionMode="multiple"
        selectedKeys={[selectedBrand?.id?.toString() as Key]}
        itemClassName={Styles.brandKitItem}
      >
        <Section>
          {
            brandKits?.map((brandKit) => (
              <SelectableItem type="item" key={brandKit.id} textValue={brandKit.name}>
                <BrandKitLogo brandKit={brandKit} size={20} />
                <span className={Styles.name}>{brandKit.name}</span>
              </SelectableItem>
            )) as ItemElement<HTMLElement>[]
          }
        </Section>
        {allowCreate && (
          <Section>
            <Item key="create" textValue="Create New Brand Kit">
              Create New Brand Kit
            </Item>
          </Section>
        )}
      </Menu>
    </MenuTrigger>
  )
}

export { BrandKitField }
